import { SimulationType } from 'store/marketing/types'
import { PromoCode, ReferralCode } from 'types/promo-referral-codes'
import { Region } from 'types/region'

// TYPES

export type SimulationPropTypes = {
  endSimulation: () => void
}

export type LeaveInfoDuringDowntimeStepPropTypes = {
  endSimulation: () => void
}

export type SubscribeStepPropTypes = {
  endSimulation?: () => void
}

export type DeliveryPoint = {
  address: {
    postalCode: string
    townCode: string
    townName: string
  }
}

export type ElectricityUsage = {
  total: number
  day: number
  night: number
  exclusive_night: number
}

export type FixedPrices = {
  electricity: number
  gas: number
}

export type MeterUsage = {
  singleRate: number
  exclNight: number
  gas: number
  doubleRate: {
    day: number
    night: number
  }
}

export type Price = {
  total: number
  totalWithoutVAT: number
  vat: number
  totalEnergy: number
  subscription: number
  transport: number
  discount: number
  injection?: number
}

export type Product = {
  electricity?: HGLELProduct
  gas?: HGLNGProduct
}

export type SimulatePriceRequest = {
  isCompany: boolean
  knowsUsage: boolean
  exclNight: boolean
  needsGas: boolean
  region: Region
  counterType?: CounterTypes
  hasSolarPanels: boolean
  solarKv: number
  product: Product
  meter: MeterType
  meterUsage: MeterUsage
  usageType: UsageType
  deliveryPoint: DeliveryPoint
  discountCode?: PromoCode
  referralCode?: ReferralCode
  price?: {
    electricity: {
      inclVat: number
      exclVat: number
    }
    gas: {
      inclVat: number
      exclVat: number
    }
  }
  fixedPrices?: FixedPrices
  extraInstalment: number
  simulationType: SimulationType
}

export type SimulatePriceResponse = {
  total: Price
  electricity: Price
  gas?: Price
  usage: MeterUsage
}

export type Simulation = {
  isCompany: boolean
  knowsUsage: boolean
  exclNight: boolean
  needsGas: boolean
  hasSolarPanels: boolean
  solarKv: number
  counterType?: CounterTypes
  product: Product
  meter: MeterType
  meterUsage: MeterUsage
  price: {
    electricity: number
    gas: number
  }
  fixedPrices?: FixedPrices
  usageType: UsageType
  deliveryPoint: DeliveryPoint
  discountCode?: PromoCode
  referralCode?: ReferralCode
}

export type UsageType = {
  residential?: {
    people: People
    house: BuildingType
  }
  commercial?: BuildingType
}

// ENUMS

export enum CounterTypes {
  ANALOG = 'Analog',
  DIGITAL = 'Digital'
}

export enum BuildingType {
  Soho = 'Soho',
  Shop = 'Shop',
  Office = 'Office',
  Industry = 'Industry',
  House = 'House',
  Apartment = 'Apartment'
}

export enum ELProduct {
  Fixed = 'Fixed',
  VariableOff = 'VariableOff',
  VariableOn = 'VariableOn',
  Go = 'Go'
}

export enum HGLELProduct {
  Fixed = 'Fix Electricity',
  VariableOff = 'Variable Belpex Electricity - Offline',
  VariableOn = 'Variable Belpex Electricity - Online',
  Go = 'Variable Belpex Electricity - Go'
}

export enum HGLNGProduct {
  Fixed = 'Fixed Gas',
  VariableOff = 'Variable TTF Gas - Offline',
  VariableOn = 'Variable TTF Gas - Online',
  Go = 'Variable TTF Gas - Go'
}

export enum MeterType {
  SINGLE_RATE = 'singleRate',
  DOUBLE_RATE = 'doubleRate'
}

export enum NGProduct {
  Fixed = 'Fixed',
  VariableOff = 'VariableOff',
  VariableOn = 'VariableOn',
  Go = 'Go'
}

export enum People {
  One = 1,
  Two = 2,
  Three = 3,
  FourOrMore = 4
}

export enum SimulationSteps {
  POSTAL_CODE = 'Postal Code',
  BUYER_TYPE = 'Buyer Type',
  HAS_SOLAR_PANELS = 'Has Solar Panels',
  COUNTER_TYPE = 'Counter type',
  SOLAR_POWER = 'Solar Power',
  ENERGY_TYPE = 'Energy Type',
  KNOWS_USAGE = 'Knows Usage',
  METER_TYPE = 'Meter Type',
  BUILDING_TYPE = 'Building Type',
  RESIDENTS = 'Residents',
  USAGE = 'Usage',
  SUBSCRIBE = 'Subscribe'
}

export enum UsageTypes {
  SINGLE_RATE = 'single_rate',
  DOUBLE_RATE_DAY = 'double_rate_day',
  DOUBLE_RATE_NIGHT = 'double_rate_night',
  EXCLUSIVE_NIGHT = 'exclusive_night',
  GAS = 'gas'
}

// REACT HOOK FORM INPUT TYPES

export type LeaveInfoDuringDowntimeFormInputs = {
  firstName: string
  lastName: string
  email: string
}

export type PostalCodeFormInputs = {
  postalCode: number
  town: string
}

export type SubscribeFormInputs = {
  firstname: string
  lastname: string
  email: string
  phone: string
  permission: boolean
}
